// MapButtons.jsx – user panel (real username + dropdown) + tools + weather
import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import leafletImage from "leaflet-image";
import "./MapButtons.css";
import AddImageIcon from "../assets/icons/Addimage.svg";
import PixelSelectIcon from "../assets/icons/Pixleselect.svg";
import PolygonSelectIcon from "../assets/icons/polygonselect.svg";
import PlusIcon from "../assets/icons/Plus.svg";
import MinusIcon from "../assets/icons/Minus.svg";
import DownloadIcon from "../assets/icons/Download.svg";
import ExitIcon from "../assets/icons/icons8_exit.svg";
import ArrowDownIcon from "../assets/icons/Arrow_down.svg";
import WeatherWidget from "./WeatherWidget";
import { useAuth } from "../hooks/useAuth";

const MapButtons = ({
  activeTool,
  setActiveTool,
  mapRef,
  weatherCenter,
  onLogout,
}) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth(onLogout);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Display name: full_name → username → "کاربر"
  const displayName =
    user?.full_name?.trim() || user?.username?.trim() || "کاربر";

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [dropdownOpen]);

  const handleToolClick = (tool) => {
    if (typeof setActiveTool === "function") {
      setActiveTool(activeTool === tool ? null : tool);
    }
  };

  const handleZoom = (action) => {
    if (!mapRef?.current) return;
    const map = mapRef.current;
    if (typeof map?.zoomIn !== "function") return;
    if (action === "in") map.zoomIn();
    else if (action === "out") map.zoomOut();
  };

  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = () => {
    if (!mapRef?.current) return;
    const map = mapRef.current;

    // Prevent double-clicks while rendering
    if (isDownloading) return;
    setIsDownloading(true);

    leafletImage(map, (err, canvas) => {
      setIsDownloading(false);

      if (err) {
        console.error("Map screenshot failed:", err);
        return;
      }

      // Build a timestamp string for the filename (e.g. map_1403-05-12_14-30.png)
      const now = new Date();
      const pad = (n) => String(n).padStart(2, "0");
      const timestamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}_${pad(now.getHours())}-${pad(now.getMinutes())}`;

      // Trigger browser download
      const link = document.createElement("a");
      link.download = `map_${timestamp}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    });
  };

  const handleLogout = () => {
    setDropdownOpen(false);
    logout();
  };

  const handleProfile = () => {
    setDropdownOpen(false);
    navigate("/profile");
  };

  return (
    <div className="controls-tl">
      {/* ── User Panel ───────────────────────────────────────────── */}
      <div className="user-panel-wrapper" ref={dropdownRef}>
        <button
          className="control-pill"
          onClick={() => setDropdownOpen((prev) => !prev)}
          aria-haspopup="true"
          aria-expanded={dropdownOpen}
        >
          <div className="user-pill-avatar" aria-hidden="true">
            {displayName.charAt(0).toUpperCase()}
          </div>
          <div className="user-pill-info">
            <span className="user-pill-label">پنل کاربر</span>
            <span className="user-pill-name" title={displayName}>
              {displayName}
            </span>
          </div>
          <img
            src={ArrowDownIcon}
            alt=""
            className={`user-pill-arrow ${dropdownOpen ? "open" : ""}`}
            aria-hidden="true"
          />
        </button>

        {/* Dropdown */}
        {dropdownOpen && (
          <div className="user-dropdown" role="menu">
            <button
              className="user-dropdown-item"
              role="menuitem"
              onClick={handleProfile}
            >
              <span className="user-dropdown-item-icon">👤</span>
              پروفایل کاربری
            </button>
            <div className="user-dropdown-divider" />
            <button
              className="user-dropdown-item danger"
              role="menuitem"
              onClick={handleLogout}
            >
              <img src={ExitIcon} alt="" className="user-dropdown-exit-icon" />
              خروج از سیستم
            </button>
          </div>
        )}
      </div>

      {/* ── Tools + Zoom ─────────────────────────────────────────── */}
      <div className="toolbar-row">
        <div className="toolbar-group toolbar-horizontal">
          <button
            className={`tool-btn ${activeTool === "addImage" ? "active" : ""}`}
            onClick={() => handleToolClick("addImage")}
            title="افزودن تصویر"
          >
            <img src={AddImageIcon} alt="Add" style={{ width: 18, height: 18 }} />
          </button>
          <button
            className={`tool-btn ${activeTool === "pixelSelect" ? "active" : ""}`}
            onClick={() => handleToolClick("pixelSelect")}
            title="انتخاب پیکسل"
          >
            <img src={PixelSelectIcon} alt="Pixel" style={{ width: 18, height: 18 }} />
          </button>
          <button
            className={`tool-btn ${activeTool === "polygonSelect" ? "active" : ""}`}
            onClick={() => handleToolClick("polygonSelect")}
            title="انتخاب چندضلعی"
          >
            <img src={PolygonSelectIcon} alt="Polygon" style={{ width: 18, height: 18 }} />
          </button>
        </div>

        <div className="toolbar-group toolbar-horizontal">
          <button className="tool-btn" onClick={() => handleZoom("in")} title="بزرگ‌نمایی">
            <img src={PlusIcon} alt="Zoom In" style={{ width: 18, height: 18 }} />
          </button>
          <button className="tool-btn" onClick={() => handleZoom("out")} title="کوچک‌نمایی">
            <img src={MinusIcon} alt="Zoom Out" style={{ width: 18, height: 18 }} />
          </button>
        </div>
      </div>

      {/* ── Weather Widget ───────────────────────────────────────── */}
      <WeatherWidget center={weatherCenter} />

      {/* ── Download ────────────────────────────────────────────── */}
      <button
        className={`download-btn ${isDownloading ? "downloading" : ""}`}
        onClick={handleDownload}
        type="button"
        disabled={isDownloading}
        title="دانلود تصویر از نقشه"
      >
        <img
          src={DownloadIcon}
          style={{
            width: 24,
            height: 24,
            opacity: isDownloading ? 0.4 : 1,
          }}
          alt=""
        />
        <span>{isDownloading ? "در حال آماده‌سازی..." : "دانلود نقشه"}</span>
      </button>
    </div>
  );
};

export default MapButtons;
